import {AxiosResponse, CancelToken} from 'axios';
import {RelationshipDirection} from '../backend/enums/relationship-direction';
import {Invite} from '../backend/interfaces/invite';
import {RelationshipInviteData} from '../backend/interfaces/relationship-invite-data';
import {PatchResolveInvite} from '../backend/requests/patch-resolve-invite';
import {
  AffiliationInviteDataPostInvite,
  PostInvite,
  RelationshipInviteDataPostInvite,
} from '../backend/requests/post-invite';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class InvitesRepository {
  // Singleton Instance
  private static _instance: InvitesRepository;

  // Private
  private logger: Logger;

  static get instance(): InvitesRepository {
    if (!this._instance) {
      this._instance = new InvitesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('InvitesRepository');
  }

  // Get an invite using the voucher code
  public async getInviteByVoucher<T>(code: string, cancelToken?: CancelToken): Promise<Invite<T>> {
    // Get the invite by voucher code
    const response: AxiosResponse<Invite<T>> = await BackendApiManager.instance.get<Invite<T>>(
      `/invites/voucher?code=${code?.trim()}`,
      {
        cancelToken,
      }
    );

    return response.data;
  }

  // Get an invite by id
  public async getInviteById<T>(id: string, cancelToken?: CancelToken): Promise<Invite<T>> {
    // Get the invite by id
    const response: AxiosResponse<Invite<T>> = await BackendApiManager.instance.get<Invite<T>>(
      `/invites/${id}`,
      {
        cancelToken,
      }
    );

    return response.data;
  }

  // Resolve (accept/refuse) an invite
  public async resolveInvite<T>(
    id: string,
    flag: boolean,
    body: PatchResolveInvite
  ): Promise<Invite<T>> {
    // Resolve the invite
    const response: AxiosResponse<Invite<T>> = await BackendApiManager.instance.patch<Invite<T>>(
      `/invites/${id}/resolve/${flag}`,
      body
    );

    return response.data;
  }

  // Create an invite
  public async createInvite<T>(
    body: PostInvite<RelationshipInviteDataPostInvite | AffiliationInviteDataPostInvite>
  ): Promise<Invite<T>> {
    // Create invite
    const response: AxiosResponse<Invite<T>> = await BackendApiManager.instance.post<Invite<T>>(
      '/invites',
      body
    );

    return response.data;
  }

  // Get the relationship invites associated with a user
  public async getRelationshipInvitesForUser(
    userId: string,
    limit: number,
    skip: number,
    direction: RelationshipDirection | undefined
  ): Promise<{totalCount: number; data: Array<Invite<RelationshipInviteData>>}> {
    // Set url
    let url: string = `/users/${userId}/relationship-invites?skip=${skip}&limit=${limit}`;

    // Add direction if provided
    if (direction) {
      url += `&direction=${direction}`;
    }

    // Get invites from backend
    const response: AxiosResponse<Array<Invite<RelationshipInviteData>>> =
      await BackendApiManager.instance.get<Array<Invite<RelationshipInviteData>>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Cancel an invite
  public async cancelInvite(inviteId: string): Promise<void> {
    // Cancel the invite
    const response: AxiosResponse<void> = await BackendApiManager.instance.delete<void>(
      `/invites/${inviteId}`
    );

    return response.data;
  }
}
